import { dateRange, degree, escapeHtml, fullName, locationText, mdToHtml, type TailoredResume } from "./markdown";

export const meta = { id: "classic", name: "Classic" };

const CSS = `
  :root { --ink: #111; --muted: #444; --rule: #000; }
  * { box-sizing: border-box; }
  body { margin: 0; background: #e9e9ec; color: var(--ink);
    font-family: Georgia, "Times New Roman", Times, serif; }
  .page { max-width: 8.5in; margin: 24px auto; background: #fff; padding: 0.7in 0.75in;
    box-shadow: 0 1px 6px rgba(0,0,0,0.15); }
  header { text-align: center; }
  h1 { font-size: 25px; margin: 0; }
  .title { font-style: italic; color: var(--muted); margin: 3px 0 8px; font-size: 14px; }
  .contact { font-size: 11.5px; color: var(--muted); line-height: 1.5; }
  .contact a { color: var(--muted); text-decoration: none; }
  .head-rule { border: none; border-top: 1.5px solid var(--rule); margin: 12px 0 4px; }
  section { margin-top: 16px; }
  h2 { font-size: 12px; text-transform: uppercase; font-weight: bold;
    border-bottom: 1px solid var(--rule); padding-bottom: 3px; margin: 0 0 8px; }
  .body { font-size: 11px; line-height: 1.4; }
  .body p { margin: 0 0 6px; }
  .body ul { margin: 4px 0 8px; padding-left: 18px; }
  .body li { margin: 2px 0; }
  .entry { margin-bottom: 11px; }
  .entry:last-child { margin-bottom: 0; }
  .row { display: flex; justify-content: space-between; gap: 12px; align-items: baseline; }
  .role { font-weight: bold; font-size: 12px; }
  .org { font-style: italic; }
  .meta { color: var(--muted); font-size: 10.5px; white-space: nowrap; }
  .rel { font-size: 10.5px; color: var(--muted); margin-top: 2px; }
  .rel ul { display: inline; margin: 0; padding: 0; }
  .rel li { display: inline; }
  .rel li:not(:last-child)::after { content: ", "; }
  .inline-list { font-size: 11px; }
  .ref { margin-bottom: 8px; font-size: 11px; }
  .ref .name { font-weight: bold; }
  @media print {
    body { background: #fff; }
    .page { box-shadow: none; margin: 0; max-width: none; padding: 0.4in 0.5in; }
  }
`;

const has = (v: unknown) => v != null && (Array.isArray(v) ? v.length > 0 : String(v).trim() !== "");
const section = (title: string, inner: string) =>
	has(inner) ? `<section><h2>${title}</h2><div class="body">${inner}</div></section>` : "";

export function renderBody(r: TailoredResume): string {
	const c = (r.contact || {}) as TailoredResume["contact"];
	const line1 = [c.email, c.phone, locationText(c.location)].filter(Boolean).join("  •  ");
	const header = `<header>
      <h1>${escapeHtml(fullName(c))}</h1>
      ${c.title ? `<div class="title">${escapeHtml(c.title)}</div>` : ""}
      <div class="contact">${line1}</div>
    </header>`;
	const experience = (r.workExperience || [])
		.map(
			(j) => `<div class="entry">
      <div class="row"><span class="role">${escapeHtml(j.jobTitle || "")}</span><span class="meta">${escapeHtml(dateRange(j))}</span></div>
      <div class="row"><span class="org">${[j.employer].filter(Boolean).map(escapeHtml).join("")}</span><span class="meta">${escapeHtml(locationText(j.location))}</span></div>
      ${has(j.highlights) ? mdToHtml(j.highlights) : ""}
      ${has(j.relevantSkills) ? `<div class="rel">Relevant skills: ${mdToHtml(j.relevantSkills)}</div>` : ""}
    </div>`,
		)
		.join("");
	const education = (r.education || [])
		.map(
			(e) => `<div class="entry">
      <div class="row"><span class="role">${escapeHtml(degree(e))}</span><span class="meta">${escapeHtml(e.graduation || "")}</span></div>
      <div class="row"><span class="org">${escapeHtml(e.schoolName || "")}</span><span class="meta">${escapeHtml(locationText(e.location))}</span></div>
      ${has(e.details) ? mdToHtml(e.details) : ""}
    </div>`,
		)
		.join("");
	const languages = `<div class="inline-list">${(r.languages || []).map((l) => `${escapeHtml(l.language)}${l.proficiency ? ` (${escapeHtml(l.proficiency)})` : ""}`).join("  •  ")}</div>`;
	const references = (r.references || [])
		.map(
			(ref) => `<div class="ref">
      <span class="name">${escapeHtml(ref.name || "")}</span>${ref.designation ? `, ${escapeHtml(ref.designation)}` : ""}${ref.workPlace ? ` — ${escapeHtml(ref.workPlace)}` : ""}<br />
      ${[locationText(ref.location), ref.phone, ref.email].filter(Boolean).map(escapeHtml).join("  •  ")}
    </div>`,
		)
		.join("");
	const customSections = (r.customSections || [])
		.map((s) => section(escapeHtml(s.title), has(s.content) ? mdToHtml(s.content) : ""))
		.join("");
	const linksList = (r.personalLinks || [])
		.map((l) => `<div>${l.type ? escapeHtml(l.type) + ": " : ""}${escapeHtml(l.value)}</div>`)
		.join("");
	return [
		header,
		section("Summary", has(r.summary) ? mdToHtml(r.summary) : ""),
		section("Skills", has(r.skills) ? mdToHtml(r.skills) : ""),
		section("Experience", experience),
		section("Education", education),
		section("Certifications", has(r.certifications) ? mdToHtml(r.certifications) : ""),
		customSections,
		section("Accomplishments", has(r.accomplishments) ? mdToHtml(r.accomplishments) : ""),
		section("Affiliations", has(r.affiliations) ? mdToHtml(r.affiliations) : ""),
		section("Languages", has(r.languages) ? languages : ""),
		section("Additional Information", has(r.additionalInfo) ? mdToHtml(r.additionalInfo) : ""),
		section("Links", has(r.personalLinks) ? linksList : ""),
		section("References", references),
	].join("");
}

export function renderDocument(resume: TailoredResume): string {
	return `<!doctype html><html><head><meta charset="utf-8" /><style>${CSS}</style></head><body><div class="page" id="resume">${renderBody(resume)}</div></body></html>`;
}
